// components/CampaignPlayer.jsx
import React, { useState, useEffect, useRef } from 'react';
import MascotCharacter from './MascotCharacter';

/**
 * Campaign Player Component
 * Displays rotating advertising campaigns for a specific screen
 * Supports images, videos, and interactive mascots
 * 
 * Props:
 * - screenId: UUID of the screen
 * - apiBase: API base URL
 * - onPlaybackStateChange: Optional callback when playback state changes (mascot-only, etc.)
 */
export default function CampaignPlayer({ screenId, apiBase, onPlaybackStateChange }) {
  const [playbackContent, setPlaybackContent] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mascots, setMascots] = useState([]); // Store mascot items separately
  
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (screenId) {
      loadPlaybackContent();
    }
  }, [screenId, apiBase]);

  useEffect(() => {
    if (playbackContent?.playlist?.length > 0) {
      playCurrentMedia();
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentIndex, playbackContent]);

  async function loadPlaybackContent() {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${apiBase}/rcc-api/advertising/screens/${screenId}/playback-content`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Flatten all media from all campaigns
      // Separate mascots (always visible) from regular media (rotating)
      const allMedia = [];
      const mascotItems = [];
      
      data.campaigns?.forEach(campaign => {
        campaign.media?.forEach(mediaItem => {
          const item = {
            ...mediaItem,
            campaign_name: campaign.campaign_name,
            campaign_id: campaign.campaign_id
          };
          
          if (mediaItem.media_type === 'mascot') {
            mascotItems.push(item);
          } else {
            allMedia.push(item);
          }
        });
      });
      
      setMascots(mascotItems);
      setPlaybackContent({ 
        campaigns: data.campaigns,
        playlist: allMedia 
      });
      setCurrentIndex(0);
      
      // Notify parent about playback state
      if (onPlaybackStateChange) {
        onPlaybackStateChange({
          mascotOnly: allMedia.length === 0 && mascotItems.length > 0,
          hasMedia: allMedia.length > 0,
          hasMascots: mascotItems.length > 0
        });
      }
    } catch (err) {
      console.error('Failed to load campaign content:', err);
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }

  function playCurrentMedia() {
    if (!playbackContent?.playlist?.length) return;
    
    const media = playbackContent.playlist[currentIndex];
    const duration = media.duration_seconds || 10;

    if (media.media_type === 'video' && videoRef.current) {
      videoRef.current.onended = advanceToNext;
      videoRef.current.play().catch(err => {
        console.error('Video play error:', err);
        advanceToNext();
      });
    } else {
      timerRef.current = setTimeout(advanceToNext, duration * 1000);
    }
  }

  function advanceToNext() {
    if (!playbackContent?.playlist?.length) return;
    setCurrentIndex(prev => (prev + 1) % playbackContent.playlist.length);
  }

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading campaigns...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <span style={{ fontSize: '48px' }}>⚠️</span>
          <p style={styles.errorText}>No campaigns available</p>
          <p style={styles.errorSubtext}>{error}</p>
        </div>
      </div>
    );
  }

  // No content state - check both playlist AND mascots
  if (!playbackContent?.playlist?.length && !mascots?.length) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>
          <span style={{ fontSize: '64px', opacity: 0.3 }}>📺</span>
          <p style={styles.emptyText}>No campaigns assigned</p>
          <p style={styles.emptySubtext}>Configure campaigns in the admin panel</p>
        </div>
      </div>
    );
  }

  // Mascot-only campaign (no rotating media)
  if (!playbackContent?.playlist?.length && mascots?.length > 0) {
    return (
      <div style={{...styles.container, background: 'transparent', pointerEvents: 'none'}}>
        {/* Transparent background - kiosk content visible underneath */}
        
        {/* Render mascots */}
        {mascots.map((mascotItem) => (
          <MascotCharacter 
            key={mascotItem.media_id}
            mediaItem={mascotItem}
            apiBase={apiBase}
          />
        ))}
      </div>
    );
  }

  // Playing state
  const currentMedia = playbackContent.playlist[currentIndex];
  const mediaUrl = `${apiBase}/uploads/${currentMedia.file_path}`;

  return (
    <div style={styles.container}>
      <div style={styles.mediaContainer}>
        {currentMedia.media_type === 'image' ? (
          <img 
            src={mediaUrl}
            alt={currentMedia.filename}
            style={styles.media}
          />
        ) : (
          <video
            ref={videoRef}
            src={mediaUrl}
            style={styles.media}
            muted
            autoPlay
            playsInline
          />
        )}
      </div>
      
      {/* Render mascots on top of media */}
      {mascots.map((mascotItem) => (
        <MascotCharacter 
          key={mascotItem.media_id}
          mediaItem={mascotItem}
          apiBase={apiBase}
        />
      ))}
      
      {/* Optional: Info overlay */}
      <div style={styles.info}>
        <span style={styles.infoText}>
          {currentMedia.campaign_name} • {currentIndex + 1}/{playbackContent.playlist.length}
        </span>
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    height: '100%',
    background: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden'
  },
  mediaContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  media: {
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain'
  },
  loading: {
    textAlign: 'center',
    color: '#fff'
  },
  spinner: {
    width: '60px',
    height: '60px',
    border: '4px solid rgba(255, 255, 255, 0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px'
  },
  loadingText: {
    fontSize: '18px',
    color: 'rgba(255, 255, 255, 0.8)',
    margin: 0
  },
  error: {
    textAlign: 'center',
    color: '#fff'
  },
  errorText: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: '16px 0 8px',
    color: '#ef4444'
  },
  errorSubtext: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.6)',
    margin: 0
  },
  empty: {
    textAlign: 'center',
    color: '#666'
  },
  emptyText: {
    fontSize: '24px',
    fontWeight: '600',
    margin: '16px 0 8px',
    color: '#888'
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#666',
    margin: 0
  },
  info: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    padding: '8px 16px',
    display: 'flex',
    justifyContent: 'center'
  },
  infoText: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '14px',
    fontWeight: '500'
  },
  mascotOnlyBackground: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandMessage: {
    fontSize: '48px',
    color: 'white',
    textAlign: 'center',
    padding: '40px',
  }
};

// Add keyframe animation for spinner
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}
